import React, { FormEvent, useRef, useEffect, MouseEvent, useState } from 'react';
import Accordion, { HeaderPrimaryItem, HeaderDetailItem, ExpandAccordion } from '../../shared/Accordion';
import { ResourceStrings } from '../../../ResourceStrings';
import { Checkbox } from '@fluentui/react/lib/Checkbox';
import styled from 'styled-components/macro';
import Button from '../../shared/Button';
import * as Actions from '../../../store/actions';
import { useSelector } from 'react-redux';
import { RootState } from '../../../store/rootState';
import { OperationState, FileInfo, ReportType, AttachmentType } from '../../../store/appState';
import ModalDialogSyslogs from './ModalDialogSyslogs';
import { showIf } from '../../../utils/react';
import { NativeActions, FileSelection } from '../../../communication/PlatformActions';
import { platformActions } from '../../../communication/Communication';
import * as urls from '../../../constants/urls';
import { Icon } from '@fluentui/react/lib/Icon';
import { getFileName } from '../../../utils/files';
import Dialog, { DialogType } from '@fluentui/react/lib/Dialog';
import { useHasChanged } from '../../../hooks/core';
import { stringFromErrorOrAny } from '../../../utils/tools';
import { App } from '../../../app';
import { TelemetryResult } from '../../../telemetry/TelemetryHelper';
import { TooltipHost } from '@fluentui/react/lib/Tooltip';
import { DirectionalHint } from '@fluentui/react/lib/Callout';
import { forMinWidth } from '../../../theme/breakpoints';

const sysLogsCountToShowByDefault = 10;

export interface AttachmentInfo extends FileInfo {
    isProcessing?: boolean;    // Is attachment still being processed
    onRemove?: () => void;
}

export default function AttachmentList({ reportType, disabled }: { reportType: ReportType; disabled?: boolean }): JSX.Element {
    const [showAllSysLogs, setShowAllSysLogs] = useState(false);
    const state = useSelector((state: RootState) => state.appState);
    const refState = useRef(state);
    const [dialogText, setDialogText] = useState<string | JSX.Element>(null);
    const hasOtherAttachmenListChanged = useHasChanged(state.reportFilesToUpload, true);
    const refExpandAccordion = useRef<ExpandAccordion>(null);

    refState.current = state;

    useEffect(() => {
        Actions.rapGetSystemLogsToUpload();
    }, [state.dialog]);

    const setIncludeSystemLogs = (shouldIncludeSystemLogs: boolean): void => {
        // @TODO: See if still needed?
        // Workaround for Riot.JS - checkbox needs to be updated AFTER other updates for screen readers to read it
        // // refChkIncludeSystemLogs.current.checked = shouldIncludeSystemLogs;
        // // App.WorkaroundScreenReader(() => {
        // //     this.chkIncludeSystemLogs.setAttribute('aria-checked', shouldIncludeSystemLogs ? "true" : "false");
        // // });

        App.telemetry.postBusinessEvent("include_system_logs-clicked", {
            selected: shouldIncludeSystemLogs ? "Yes" : "No",
        });
        Actions.rapUpdateIncludeSystemLogs(shouldIncludeSystemLogs);
    }

    const includeSystemLogsChanged = (e: FormEvent): void => {
        const shouldIncludeSystemLogs: boolean = !refState.current.reportIncludeSystemLogs;
        if (!shouldIncludeSystemLogs) {
            e.preventDefault();
            Actions.setShowDialogSysLogs(true);
            return;
        }

        setIncludeSystemLogs(true);
    }

    const dialogSyslogsResult = (action: "include" | "exclude"): void => {
        Actions.setShowDialogSysLogs(false);

        const shouldIncludeSystemLogs: boolean = (action === "include");
        setIncludeSystemLogs(shouldIncludeSystemLogs); // always send update, because checkbox control is checked/unchecked from the UI unintentionally on click
    }

    const clickFileLink = (e: MouseEvent, isProcessing: boolean): void => {
        if (e.target == null) {
            return;
        }
        e.preventDefault();
        if (isProcessing) {
            setDialogText(ResourceStrings.processingFile());
            return;
        }

        const filePath: string = (e.target as HTMLAnchorElement).getAttribute("data-file");
        const native: NativeActions = platformActions.getNativeActions();
        if (native == null) {
            return;
        }

        native.viewFile(filePath)
            .catch((e) => {
                App.showUnexpectedError(ResourceStrings.viewFileError(stringFromErrorOrAny(e)));
            });
    }

    const deleteClick = (item: FileInfo): void => {
        App.telemetry.postBusinessEvent(App.getEventNameForReportType("removefile-clicked", reportType), {
            attachedFileCount: refState.current.reportFilesToUpload.length.toString(),
        });

        Actions.rapRemoveFileToUpload(item);
        // @TODO: Set focus to first item in list
        //////if (this.props.state.rapView === RapView.screenshot) {
        //////    this.refBtnAttachFilesScreenshot.current.focus();
        //////}
        //////else {
        //////    this.refBtnAttachFilesRecord.current.focus();
        //////}
    }

    const attachClick = (): void => {
        // // const location: 'screenshot' | 'record' = ((e.target as HTMLElement).getAttribute('data-location') || 'screenshot') as any;

        App.telemetry.postBusinessEvent(App.getEventNameForReportType("attachfiles-clicked", reportType), {
            attachedFileCount: refState.current.reportFilesToUpload.length.toString(),
        });

        const userTask = App.telemetry.startUserTask("Attach");
        const native: NativeActions = platformActions.getNativeActions();
        if (native == null) {
            return; // @TODO: support file attachments just on website (without native side)
        }

        native.selectFiles().then((fileSelection: FileSelection) => {
            console.log("SELECTED_FILES");
            console.log(fileSelection);
            const files: string[] = fileSelection?.includedFilePaths;
            if (!files || (files.length === 0 && fileSelection.deniedFilePaths.length === 0)) {
                userTask.end(TelemetryResult.UserCancel, "Attach cancelled");
                return;
            }

            if (fileSelection.deniedFilePaths.length > 0) {
                // @TODO: show message to user that some of their files were excluded by policy
            }

            const fileInfos: FileInfo[] = files.map((file: string) => {
                return { path: file, name: getFileName(file), isUserFile: true, fileType: AttachmentType.userAttachedFile };
            });

            Actions.rapAddFilesToUpload(fileInfos);

            userTask.end(TelemetryResult.Success, `Success in attaching ${files.length.toString()} files: ${files.join()}`);

            // @TODO: focus last attachment
            // focus last attachment
            // // const elems: HTMLCollectionOf<Element> = document.getElementsByClassName(`attachmentx-${location}`);
            // // if (elems.length > 0) {
            // //     (elems[elems.length - 1] as HTMLElement).focus();
            // // }
        }).catch(reason => {
            userTask.end(TelemetryResult.Failure, `Error while attaching ${reason}`);

            App.showUnexpectedError(ResourceStrings.selectFileError(stringFromErrorOrAny(reason)));
        });
    }

    let sysLogs: FileInfo[] = [];
    if (state.reportIncludeSystemLogs) {
        if (state.reportSystemFilesToUpload) {
            sysLogs = sysLogs.concat(state.reportSystemFilesToUpload);
        }
    }

    let otherAttachments: AttachmentInfo[] = [];
    if (state.reportFilesToUpload && state.reportFilesToUpload.length !== 0) {
        otherAttachments = otherAttachments.concat(
            state.reportFilesToUpload.map(file => ({ ...file, isProcessing: state.filesBeingProcessed.indexOf(file.path) !== -1 }))
        );
    }

    if (state.reportIncludeScreenshot) {
        otherAttachments.unshift({
            name: ResourceStrings.screenshot(),
            fileType: AttachmentType.screenshot,
            onRemove: () => {
                Actions.rapUpdateIncludeScreenshot(false)
                if (state.reportIncludeScreenshot && state.reportIsCropping) {
                    Actions.setIsCropping(false);
                }
            }
        } as AttachmentInfo);
    }

    if (state.reportIncludeScreenRecording && state.screenRecording && state.screenRecording.size > 0) {
        const sizeMb = (state.screenRecording.size / (1024 * 1024)).toFixed(2);
        otherAttachments.unshift({
            name: `Screen Recording (${sizeMb} MB)`,
            fileType: AttachmentType.screenRecording,
            onRemove: () => {
                Actions.rapIncludeScreenRecording(false, null);
            }
        } as AttachmentInfo);
    }

    const sysLogsToShow = showAllSysLogs
        ? sysLogs
        : sysLogs.slice(0, sysLogsCountToShowByDefault);

    useEffect(() => {
        // Expand attachments pane if attachments have been added in background e.g. by Action recorder
        if (hasOtherAttachmenListChanged && otherAttachments.length !== 0) {
            refExpandAccordion.current?.();
        }
    })

    return (
        <Accordion
            titleIcon="PhotoVideoMedia"
            title={<span css="display: flex">
                <HeaderPrimaryItem>{ResourceStrings.additionalAttachmentsTitle()}&nbsp;&nbsp;</HeaderPrimaryItem>
                <HeaderDetailItem> {ResourceStrings.systemLogs(sysLogs.length)}&nbsp;&nbsp;|&nbsp;&nbsp;</HeaderDetailItem>
                <HeaderDetailItem> {ResourceStrings.otherFiles(otherAttachments.length)}</HeaderDetailItem>
            </span>}
            setExpandAccordion={(expandAccordion) => { refExpandAccordion.current = expandAccordion }}
        >
            <Content>
                <div className="attachment-lists-container">
                    <SystemLogs>
                        <Checkbox
                            checked={state.reportIncludeSystemLogs}
                            disabled={disabled}
                            label={ResourceStrings.systemLogs(sysLogs.length)}
                            onChange={(e) => includeSystemLogsChanged(e)}
                        />
                        <SystemLogsList>
                            <li
                                style={showIf(state.reportGetSystemFilesState === OperationState.Executing)}
                                tabIndex={0}>
                                {ResourceStrings.loading()}
                            </li>
                            <li
                                style={showIf(state.reportGetSystemFilesState === OperationState.Failed)}
                                tabIndex={0}>
                                {ResourceStrings.systemLogsNone()}
                            </li>
                            {sysLogsToShow.map((file, idx) => (
                                <li key={idx} style={showIf(state.reportIncludeSystemLogs)}>
                                    <a
                                        href="#"
                                        data-file={file.path}
                                        onClick={(evt) => clickFileLink(evt, false)}>
                                        {file.name}
                                    </a>
                                </li>))}

                            {sysLogs.length > sysLogsCountToShowByDefault && <li>
                                <Button buttonType="link" onClick={() => setShowAllSysLogs(!showAllSysLogs)} disabled={disabled}>
                                    {showAllSysLogs ? ResourceStrings.showLess() : ResourceStrings.showAll()}
                                </Button>
                            </li>}

                            <li className="data-we-collect">
                                <a href={urls.dataWeCollect} target="_blank" rel="noopener noreferrer">{ResourceStrings.dataWeCollect()}</a>
                            </li>
                        </SystemLogsList>
                    </SystemLogs>

                    <AdditionalFiles>
                        <h3>{ResourceStrings.additionalFiles()}</h3>
                        <ul className="additional-files-list">
                            {otherAttachments.map((file, idx) => <li key={idx}>
                                <span>
                                    <AttachmentTypeIcon id={`screenshot-icon-${idx}`} fileType={file.fileType} />
                                    {file.path && (
                                        <a
                                            href="#"
                                            data-file={file.path}
                                            aria-label={`${file.path} ${file.fileType === AttachmentType.recording ? ResourceStrings.generatedByRecorder() : ''}`}
                                            onClick={(evt) => clickFileLink(evt, file.isProcessing)}>
                                            {file.name}
                                        </a>
                                    )}

                                    {!file.path && <span>{file.name}</span>}
                                </span>

                                {!file.isProcessing && <Button
                                    aria-label={ResourceStrings.delete(file.name)}
                                    size="compact"
                                    buttonType="link"
                                    disabled={disabled}
                                    onClick={() => file.onRemove ? file.onRemove() : deleteClick(file)}>
                                    <Icon iconName="clear" />
                                </Button>}
                                {file.isProcessing && <span className="dot-loader-parent"><span className="dot-loader" /></span>}
                            </li>)}
                        </ul>
                    </AdditionalFiles>
                </div>
                <BottomBar>
                    <a href={urls.dataWeCollect} target="_blank" rel="noopener noreferrer" className="data-we-collect">{ResourceStrings.dataWeCollect()}</a>
                    <Button disabled={disabled} onClick={() => attachClick()}>{ResourceStrings.attachMoreFiles()}</Button>
                </BottomBar>
                {state.showDialogSysLogs && (
                    <ModalDialogSyslogs result={(action: "include" | "exclude") => dialogSyslogsResult(action)} isOpen={state.showDialogSysLogs} />
                )}

                <Dialog
                    hidden={!dialogText}
                    onDismiss={() => setDialogText(null)}
                    maxWidth="70%"
                    dialogContentProps={{
                        type: DialogType.close,
                        closeButtonAriaLabel: ResourceStrings.close(),
                        isMultiline: true,
                        styles: { inner: { padding: 40 } }
                    }}
                    modalProps={{
                        isBlocking: false,
                    }}
                >
                    {dialogText}
                </Dialog>
            </Content>
        </Accordion>
    );
}

interface AttachmentDisplayInfo {
    icon: string;
    label: string;
}

const iconAndLabelForFileType = (fileType: AttachmentType): AttachmentDisplayInfo => {
    switch (fileType) {
        case AttachmentType.userAttachedFile: return { icon: "Attach", label: ResourceStrings.attachedByUser() };
        case AttachmentType.screenshot: return { icon: "ImageCrosshair", label: ResourceStrings.screenshot() };
        case AttachmentType.screenRecording: return { icon: "Video", label: "Screen Recording" };
        case AttachmentType.recording: return { icon: "Video", label: ResourceStrings.generatedByRecorder() };
        default: return { icon: "", label: "" };
    }
}

interface AttachmentTypeIconProps {
    id: string;
    fileType: AttachmentType;
}

const AttachmentTypeIcon = (props: AttachmentTypeIconProps): JSX.Element => {
    const { icon: attachmentTypeIcon, label: attachmentTypeLabel } = iconAndLabelForFileType(props.fileType);

    return <span className='file-type-icon'>
        <TooltipHost
            id={props.id}
            content={attachmentTypeLabel}
            directionalHint={DirectionalHint.leftCenter}
        >
            <Icon
                ariaLabel={attachmentTypeLabel}
                iconName={attachmentTypeIcon}
            />
        </TooltipHost>
    </span>
}

const Content = styled.div`
    padding: 1.4rem 2.8rem 2.8rem 2.8rem;

    ${forMinWidth.sm`
        padding: 1.4rem 4.8rem 2.8rem 4.8rem;
    `}

    > div {
        display: flex;
    }

    h3 {
        font-size: 1.8rem;
        font-weight: 600;
        margin-block-start: 0;
        margin-block-end: 0;
    }

    .dot-loader-parent {
        font-size: .2rem;
        margin-left: 3rem;
    }

    .attachment-lists-container {
        display: grid;
        grid-template-columns: minmax(15rem, max-content);


        ${forMinWidth.md`
            grid-template-columns: minmax(15rem, max-content) minmax(15rem, max-content);
        `}
    }

    .data-we-collect, .data-we-collect a {
        font-size: 1.4rem;
        font-weight: normal;
    }
`;

const SystemLogs = styled.div`
    font-size: 1.8rem;

    .ms-Checkbox {
        margin-bottom: 2.8rem;
    }

    .ms-Checkbox-text {
        font-size: 1.8rem;
        font-weight: 600;
    }

    > :not(:first-child) {
        margin-left: 2.6rem;
    }

    button {
        margin-top: 2rem;
        padding: 0.1rem .5rem;
        height: auto;
        font-size: 1.5rem;
        float: right;
    }
`;

const SystemLogsList = styled.ul`
    margin-top: 1.5rem;
    font-size: var(--font-size-default);
    font-weight: normal;
    padding-left: 0;

    li {
        list-style: none;
        margin-bottom: 0.8rem;
        line-height: 1.9rem;
        word-break: break-all;
    }

    a {
        font-size: var(--font-size-default);
    }

    .data-we-collect {
        margin-top: 5rem;
    }

    ${forMinWidth.md`
        a {
            font-weight: 600;
        }

        .data-we-collect {
            display: none;
        }
    `}
`;

const AdditionalFiles = styled.div`
    margin-top: 3rem;

    ${forMinWidth.md`
        border-left: solid 1px var(--color-on-surface-lighter);
        padding-left: 4.8rem;
        margin-left: 3.6rem;
        margin-top: 0;
    `}

    .additional-files-list {
        margin-top: 2.8rem;
        color: var(--color-on-surface);
        font-size: var(--font-size-default);
        font-weight: normal;
        line-height: 2.6rem;
        padding-left: 0;

        li {
            display: flex;
            justify-content: space-between;
            list-style: none;
            line-height: normal;
            margin-bottom: .6rem;

            button {
                font-size: 1.2rem;
                color: gray;
            }
        }

        a {
            color: var(--color-on-surface-link);
            text-decoration: none;
            word-break: break-all;
        }

        button {
            margin-left: .8rem;
        }

        .file-type-icon {
            width: 1rem;
            font-size: 1.4rem;
            margin-right: 1rem;
            vertical-align: middle;
            display: inline-block;
            cursor: default;
        }
    }
`;

const BottomBar = styled.div`
    display: flex;
    justify-content: flex-end;
    margin-top: 3.5rem;

    .data-we-collect {
        display: none;
    }

    ${forMinWidth.md`
        justify-content: space-between;

        .data-we-collect {
            display: inline;
        }
    `}
`;
